import { DatePipe, DecimalPipe, NgClass, NgComponentOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltip } from '@angular/material/tooltip';
// eslint-disable-next-line boundaries/external
import { RouterLink } from '@angular/router';
import { IsGrantedDirective } from '@app/directives/is-granted.directive';
import { SortType } from '@app/enums/sort-type.enum';
import { StatusType } from '@app/enums/status.type.enum';
import { ToastType } from '@app/enums/toast-type.enum';
import { formatString } from '@app/helpers/text/format-string';
import { DataTestIdPipe } from '@app/pipes/data-test-id/data-test-id-pipe';
import { AppStore } from '@app/stores/app/app.store';
import type {
    IColumns,
    IData,
    INotFoundContent,
    ISort,
    ITableAction,
    ITableColumnStatus,
    ITableSButtonActions,
    ITableStore,
    ITableTabs,
} from '@app/types/data-tables';
import { debounceTime, of, Subject, Subscription, switchMap } from 'rxjs';
import { NotFoundComponent } from '../not-found/not-found.component';

@Component({
    selector: 'app-data-table',
    imports: [
        MatIconModule,
        MatButtonModule,
        MatMenuModule,
        MatTooltip,
        RouterLink,
        NotFoundComponent,
        MatRippleModule,
        DatePipe,
        NgClass,
        FormsModule,
        MatTableModule,
        DecimalPipe,
        DataTestIdPipe,
        MatTabsModule,
        MatProgressSpinnerModule,
        IsGrantedDirective,
        NgComponentOutlet,
    ],
    templateUrl: './data-table.component.html',
    styleUrl: './data-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataTableComponent<T> implements OnDestroy, OnInit {
    private appStore = inject(AppStore);
    translate = this.appStore.i18n;
    readonly sortAsc = SortType.ASC;
    readonly sortDesc = SortType.DESC;
    readonly presetStatus = {
        [StatusType.Active]: {
            color: 'rcp-bg-ul-active-bg rcp-text-ul-active-text',
            icon: 'check',
        },
        [StatusType.Invited]: {
            color: 'rcp-bg-ul-invited-bg rcp-text-ul-invited-text',
            icon: 'mail',
        },
        [StatusType.Draft]: {
            color: 'rcp-bg-ul-draft-bg rcp-text-ul-draft-text',
            icon: 'history_toggle_off',
        },
        [StatusType.PendingReview]: {
            color: 'rcp-bg-ul-draft-bg rcp-text-ul-draft-text',
            icon: 'history_toggle_off',
        },
        [StatusType.Default]: {
            color: 'rcp-bg-ul-gray-200 rcp-text-ul-dark-400',
            icon: '',
        },
        [StatusType.Expired]: {
            color: 'rcp-bg-ul-expired-bg rcp-text-ul-expired-text',
            icon: '',
        },
        [StatusType.Unknown]: {
            color: 'rcp-bg-ul-gray-200 rcp-text-ul-dark-400',
            icon: '',
        },
    };
    readonly statusBadge = ['status'];
    readonly pageSizeList = [10, 25, 50, 100, 200];
    dataSource = input<
        IData<{
            id: string;
            name: string;
            email: string;
            status: string;
        }>[]
    >();
    columns = input<
        IColumns<{
            id: number;
            dataIndex: string;
            title: string;
            sort: boolean;
            date?: string;
            imagePath?: string;
            className?: string;
        }>[]
    >([]);
    rowId = input<string>('');
    tableId = input<string>('');
    tableTitle = input<string>('');
    onAddTitle = input<string>('');
    notFoundContent = input<INotFoundContent>({
        baseTitle: '',
        baseDescription: '',
        subTitle: '',
        subDescription: '',
    });
    page = input<number>(1);
    pageSize = input<number>(10);
    sortBy = input<string>('');
    tableActions = input<ITableSButtonActions>({
        label: '',
        testId: '',
        subLabel: '',
    });
    tabs = input<ITableTabs[]>([]);
    hasNoSearch = input<boolean>(false);
    fullRowSelection = input<boolean>(false);
    searchNote = input<string>('');

    onSearchAction = output();
    panelClosed = output();
    fetchData = output<ITableAction<T>>();
    onFullRowClick = output<IData<T>>();

    store = signal<ITableStore<T>>({
        data: [],
        options: {
            page: 1,
            pageSize: 10,
            totalCount: 0,
            totalPages: 0,
            search: '',
            sort: this.sortDesc,
            sortBy: '',
        },
        hasData: false,
        loading: true,
        initialLoading: true,
        columnsData: [],
        hasError: false,
    });
    inputSearch = signal<string>('');
    inputPage = signal<number>(this.page());
    tableData = computed(() => this.store().data);
    tableLoading = computed(() => this.store().loading);
    tablePage = computed(() => this.store().options.page);
    tablePageSize = computed(() => this.store().options.pageSize);
    tableTotalCount = computed(() => this.store().options.totalCount);
    tableTotalPages = computed(() => this.store().options.totalPages);
    tableSearch = computed(() => this.store().options.search);
    tableSort = computed(() => this.store().options.sort);
    tableSortBy = computed(() => this.store().options.sortBy);
    tableHasData = computed(() => this.store().hasData);
    tableInitialLoading = computed(() => this.store().initialLoading);
    tableHasError = computed(() => this.store().hasError);
    columnsData = computed(() => this.store().columnsData);

    errorContent = computed(() => {
        const { TITLE__ConnectionFailed, MESSAGE__UnknownError, RetryAgain } =
            this.translate();

        if (this.notFoundContent().retryText) {
            return {
                title: this.notFoundContent().baseTitle,
                message: this.notFoundContent().baseDescription,
                retryText: this.notFoundContent().retryText,
                onRetryAction: () => {
                    this.notFoundContentAction();
                },
            };
        }

        return {
            title: TITLE__ConnectionFailed,
            message: formatString(MESSAGE__UnknownError, ToastType.Error),
            retryText: RetryAgain,
            onRetryAction: () => {
                this.onFetchTable();
                this.store.update((prev) => ({
                    ...prev,
                    initialLoading: true,
                    options: {
                        ...prev.options,
                    },
                }));
            },
        };
    });

    private searchSubject$ = new Subject<string>();
    private searchSubscription$?: Subscription;
    private pageTypeSubject$ = new Subject<string>();
    private pageTypeSubscription$?: Subscription;

    public statusColor(status: ITableColumnStatus): string {
        const key = status
            .replace(/\s+/g, '')
            .toLocaleLowerCase() as keyof typeof this.presetStatus;
        return this.presetStatus[key].color;
    }

    public statusIcon(status: ITableColumnStatus): string {
        const key = status
            .replace(/\s+/g, '')
            .toLocaleLowerCase() as keyof typeof this.presetStatus;
        return this.presetStatus[key].icon;
    }

    ngOnInit() {
        this.initialTableFetch();

        this.store.update((prev) => ({
            ...prev,
            columnsData: [...this.columns().map((column) => column.dataIndex)],
        }));

        this.pageTypeSubscription$ = this.pageTypeSubject$
            .pipe(
                debounceTime(500),
                switchMap((searchQuery) => {
                    return of(searchQuery);
                }),
            )
            .subscribe((pageValue) => {
                this.onChangePage(Number(pageValue));
            });

        this.searchSubscription$ = this.searchSubject$
            .pipe(
                debounceTime(500),
                switchMap((searchQuery) => {
                    return of(searchQuery);
                }),
            )
            .subscribe((searchValue) => {
                this.actionFetchUsers({
                    page: this.page(),
                    pageSize: this.tablePageSize(),
                    search: searchValue,
                    sort: this.tableSort(),
                    sortBy: this.tableSortBy(),
                });
            });
    }

    ngOnDestroy(): void {
        this.searchSubscription$?.unsubscribe();
        this.pageTypeSubscription$?.unsubscribe();
    }

    initialTableFetch(): void {
        this.actionFetchUsers({
            page: this.page(),
            pageSize: this.pageSize(),
            sort: this.sortBy() ? this.sortDesc : '',
            sortBy: this.sortBy() || '',
        });
        this.store.update((prev) => ({
            ...prev,
            initialLoading: true,
            options: {
                ...prev.options,
            },
        }));
    }

    onFetchTable(): void {
        this.actionFetchUsers({
            page: this.tablePage(),
            pageSize: this.tablePageSize(),
            search: this.tableSearch(),
            sort: this.tableSort(),
            sortBy: this.tableSortBy(),
        });
    }

    onSearch(): void {
        this.searchSubject$.next(this.inputSearch());
    }

    onTypePage(): void {
        this.pageTypeSubject$.next(this.inputPage().toLocaleString());
    }

    onClearSearch(inputElement: HTMLInputElement): void {
        inputElement.value = '';
        this.inputSearch.set('');

        this.actionFetchUsers({
            page: this.page(),
            pageSize: this.tablePageSize(),
            search: '',
            sort: this.tableSort(),
            sortBy: this.tableSortBy(),
        });
    }

    onChangePageSize(event: Event): void {
        const selectValue = (event.target as HTMLSelectElement).value;

        this.actionFetchUsers({
            page: this.page(),
            pageSize: Number(selectValue),
            search: this.tableSearch(),
            sort: this.tableSort(),
            sortBy: this.tableSortBy(),
        });
    }

    onChangePage(page: number): void {
        if (page >= 1 && page <= this.tableTotalPages()) {
            this.actionFetchUsers({
                page,
                pageSize: this.tablePageSize(),
                search: this.tableSearch(),
                sort: this.tableSort(),
                sortBy: this.tableSortBy(),
            });
        }
    }

    onSortColumn(sort?: ISort, column?: string) {
        let sortValue = sort;

        switch (sort) {
            case this.sortAsc:
                sortValue = this.sortDesc;
                break;
            case this.sortDesc:
                sortValue = this.sortAsc;
                break;
            default:
                sortValue = this.sortDesc;
                break;
        }

        this.actionFetchUsers({
            page: this.tablePage(),
            pageSize: this.tablePageSize(),
            search: this.tableSearch(),
            sort: this.tableSortBy() !== column ? this.sortAsc : sortValue,
            sortBy: sortValue === '' ? '' : column,
        });
    }

    actionFetchUsers({
        page,
        pageSize,
        search,
        sort,
        sortBy,
    }: Omit<ITableAction, 'onFetch'>) {
        this.store.update((prev) => ({ ...prev, loading: true }));
        this.inputPage.set(page);
        this.fetchData.emit({
            page,
            pageSize,
            search,
            sort,
            sortBy,
            onFetch: (data, totalPages, totalCount) => {
                this.store.update((prev) => ({
                    ...prev,
                    data: data ?? [],
                    loading: false,
                    initialLoading: prev.initialLoading ? false : prev.initialLoading,
                    hasData: !prev.hasData ? (data?.length ?? 0) > 0 : prev.hasData,
                    hasError: data === null,
                    options: {
                        ...prev.options,
                        page,
                        pageSize,
                        totalCount,
                        totalPages,
                        search: search,
                        sort: sort,
                        sortBy: sortBy,
                    },
                }));
            },
        });
    }

    notFoundContentAction(): void {
        if (this.notFoundContent().onRetry) {
            this.notFoundContent().onRetry?.(() => {
                this.onFetchTable();
                this.store.update((prev) => ({
                    ...prev,
                    initialLoading: true,
                    options: {
                        ...prev.options,
                    },
                }));
            });
        }
        if (this.notFoundContent().onClick) {
            this.notFoundContent().onClick?.(() => {
                this.onFetchTable();
            });
        }
    }

    buttonAction(action: ITableSButtonActions): void {
        if (action.onClick) {
            action.onClick(this.onFetchTable.bind(this));
        }
    }

    onSelectTab(tab: ITableTabs): void {
        if (!tab.active) {
            tab.onClick(tab.name);
            this.store.update((prev) => ({
                ...prev,
                data: [],
                initialLoading: true,
                hasData: false,
            }));
            this.actionFetchUsers({
                page: this.page(),
                pageSize: this.tablePageSize(),
                sort: this.tableSort(),
                sortBy: this.tableSortBy(),
            });
        }
    }

    onFullRowClickAction(e: Event, row: IData<T>): void {
        e.preventDefault();
        if (!this.fullRowSelection()) return;
        this.onFullRowClick.emit(row);
    }
}
